import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLeaderboard } from '../hooks/useLeaderboard';
import { useLeaderboardScopes } from '../hooks/useLeaderboardScopes';
import LeaderboardBoard from '../components/LeaderboardBoard';
import ScopeChips, { ScopeChipItem } from '../components/ScopeChips';
import { slugify } from '../../infrastructure/utils/slug';
import LocalizationService from '../../application/services/LocalizationService';
import './LeaderboardPages.css';

const t = (key: string, options?: Record<string, unknown>) =>
  LocalizationService.translate(`leaderboard.${key}`, undefined, options) as string;

/** /leaderboard/:countryCode — cities in that country + the country's top referrers. */
const CountryLeaderboardPage: React.FC = () => {
  const { countryCode = '' } = useParams<{ countryCode: string }>();
  const [page, setPage] = useState(0);
  const { scopes, isLoading: scopesLoading } = useLeaderboardScopes();

  const country = useMemo(
    () => scopes?.countries.find((c) => c.code.toLowerCase() === countryCode.toLowerCase()),
    [scopes, countryCode]
  );

  const { board, isLoading, error } = useLeaderboard('COUNTRY', country?.id ?? null, page, !!country);

  if (!scopesLoading && !country) {
    return (
      <div className="lbp-root">
        <div className="lbp-not-found">
          <h1>{t('notFoundCountryTitle')}</h1>
          <p>
            <Link to="/leaderboard">{t('backToGlobal')}</Link>
          </p>
        </div>
      </div>
    );
  }

  const cityChips: ScopeChipItem[] = country
    ? (scopes?.cities ?? [])
        .filter((city) => city.country_id === country.id)
        .map((city) => ({
          key: String(city.id),
          label: city.name,
          to: `/leaderboard/${countryCode.toLowerCase()}/${slugify(city.name)}`,
        }))
    : [];

  return (
    <div className="lbp-root">
      <nav className="lbp-breadcrumb">
        <Link to="/leaderboard">{t('globalLabel')}</Link>
        <span>/</span>
        <span>{country?.name || countryCode}</span>
      </nav>

      <header className="lbp-header">
        <span className="lbp-eyebrow">{t('eyebrow')}</span>
        <h1 className="lbp-headline">{country?.name || countryCode} {t('countryHeadlineSuffix')}</h1>
        <p className="lbp-sub">{t('countrySub')}</p>
      </header>

      {cityChips.length > 0 && (
        <div className="lbp-selector">
          <span className="lbp-selector-label">{t('jumpToCity')}</span>
          <ScopeChips ariaLabel={t('jumpToCity')} items={cityChips} />
        </div>
      )}

      {(isLoading || scopesLoading) && <div className="lbp-loading">{t('loading')}</div>}
      {error && <div className="lbp-error">{error}</div>}
      {board && <LeaderboardBoard board={board} page={page} onPageChange={setPage} />}
    </div>
  );
};

export default CountryLeaderboardPage;
